import { CostCalculator, costCalculator } from './CostCalculator'
import type {
  OptimizationResult,
  OptimizationStrategy,
  OptimizationWeights,
  OptimizerDemandItem,
  SupplierOfferCandidate,
} from '../types'
import { DEFAULT_OPTIMIZATION_WEIGHTS } from '../types'
import type { CostLineInput } from './CostCalculator'

interface Assignment {
  demandId: string
  offer: SupplierOfferCandidate
}

/**
 * OptimizationEngine — strategy-driven supplier combination selection.
 */
export class OptimizationEngine {
  private weights: OptimizationWeights
  private cost: CostCalculator

  constructor(
    cost: CostCalculator = costCalculator,
    weights: OptimizationWeights = { ...DEFAULT_OPTIMIZATION_WEIGHTS },
  ) {
    this.cost = cost
    this.weights = { ...weights }
  }

  setWeights(weights: Partial<OptimizationWeights>): OptimizationWeights {
    this.weights = this.normalize({ ...this.weights, ...weights })
    return this.getWeights()
  }

  getWeights(): OptimizationWeights {
    return { ...this.weights }
  }

  optimize(
    demands: OptimizerDemandItem[],
    offers: SupplierOfferCandidate[],
    strategy: OptimizationStrategy,
    baselineTotal: number,
  ): OptimizationResult {
    const byDemand = this.groupOffers(demands, offers)
    const assignment = this.selectAssignment(demands, byDemand, strategy)
    return this.buildResult(demands, assignment, strategy, baselineTotal)
  }

  private groupOffers(
    demands: OptimizerDemandItem[],
    offers: SupplierOfferCandidate[],
  ): Map<string, SupplierOfferCandidate[]> {
    const map = new Map<string, SupplierOfferCandidate[]>()
    for (const demand of demands) {
      const matched = offers.filter(
        (o) =>
          o.demandId === demand.id &&
          o.stock >= demand.quantity &&
          o.unitPrice > 0,
      )
      matched.sort((a, b) => a.unitPrice - b.unitPrice || a.deliveryDays - b.deliveryDays)
      map.set(demand.id, matched)
    }
    return map
  }

  private selectAssignment(
    demands: OptimizerDemandItem[],
    byDemand: Map<string, SupplierOfferCandidate[]>,
    strategy: OptimizationStrategy,
  ): Assignment[] {
    switch (strategy) {
      case 'MENOR_PRECO':
        return this.pickPerItem(demands, byDemand, (a, b) => a.unitPrice - b.unitPrice)
      case 'MENOR_PRAZO':
        return this.pickPerItem(
          demands,
          byDemand,
          (a, b) => a.deliveryDays - b.deliveryDays || a.unitPrice - b.unitPrice,
        )
      case 'MENOR_FRETE':
        return this.minimizeFreight(demands, byDemand)
      case 'MENOR_NUMERO_FORNECEDORES':
        return this.minimizeSuppliers(demands, byDemand)
      case 'MELHOR_SCORE':
      case 'PERSONALIZADO':
        return this.bestScore(demands, byDemand)
      default:
        return this.pickPerItem(demands, byDemand, (a, b) => a.unitPrice - b.unitPrice)
    }
  }

  private pickPerItem(
    demands: OptimizerDemandItem[],
    byDemand: Map<string, SupplierOfferCandidate[]>,
    compare: (a: SupplierOfferCandidate, b: SupplierOfferCandidate) => number,
  ): Assignment[] {
    const result: Assignment[] = []
    for (const demand of demands) {
      const list = [...(byDemand.get(demand.id) ?? [])].sort(compare)
      const offer = list[0]
      if (offer) result.push({ demandId: demand.id, offer })
    }
    return result
  }

  /** Prefer consolidating on suppliers that unlock free freight / low freight. */
  private minimizeFreight(
    demands: OptimizerDemandItem[],
    byDemand: Map<string, SupplierOfferCandidate[]>,
  ): Assignment[] {
    const suppliers = this.allSuppliers(byDemand)
    let best: Assignment[] | null = null
    let bestFreight = Number.POSITIVE_INFINITY
    let bestTotal = Number.POSITIVE_INFINITY

    // Try single-supplier plans first
    for (const supplier of suppliers) {
      const assignment = this.assignIfCovered(demands, byDemand, new Set([supplier]))
      if (!assignment) continue
      const cost = this.costOf(demands, assignment)
      if (
        cost.freightCost < bestFreight ||
        (cost.freightCost === bestFreight && cost.totalCost < bestTotal)
      ) {
        best = assignment
        bestFreight = cost.freightCost
        bestTotal = cost.totalCost
      }
    }

    // Greedy: start from cheapest per item then swap toward lower freight
    const greedy = this.pickPerItem(demands, byDemand, (a, b) => a.unitPrice - b.unitPrice)
    const greedyCost = this.costOf(demands, greedy)
    if (
      !best ||
      greedyCost.freightCost < bestFreight ||
      (greedyCost.freightCost === bestFreight && greedyCost.totalCost < bestTotal)
    ) {
      best = greedy
      bestFreight = greedyCost.freightCost
      bestTotal = greedyCost.totalCost
    }

    // Evaluate top supplier pairs
    for (let i = 0; i < suppliers.length; i++) {
      for (let j = i + 1; j < Math.min(suppliers.length, i + 6); j++) {
        const set = new Set([suppliers[i]!, suppliers[j]!])
        const assignment = this.assignIfCovered(demands, byDemand, set)
        if (!assignment) continue
        const cost = this.costOf(demands, assignment)
        if (
          cost.freightCost < bestFreight ||
          (cost.freightCost === bestFreight && cost.totalCost < bestTotal)
        ) {
          best = assignment
          bestFreight = cost.freightCost
          bestTotal = cost.totalCost
        }
      }
    }

    return best ?? greedy
  }

  private minimizeSuppliers(
    demands: OptimizerDemandItem[],
    byDemand: Map<string, SupplierOfferCandidate[]>,
  ): Assignment[] {
    const suppliers = this.allSuppliers(byDemand)
    let best: Assignment[] | null = null
    let bestCount = Number.POSITIVE_INFINITY
    let bestTotal = Number.POSITIVE_INFINITY

    // Single supplier coverage
    for (const supplier of suppliers) {
      const assignment = this.assignIfCovered(demands, byDemand, new Set([supplier]))
      if (!assignment) continue
      const cost = this.costOf(demands, assignment)
      if (1 < bestCount || (1 === bestCount && cost.totalCost < bestTotal)) {
        best = assignment
        bestCount = 1
        bestTotal = cost.totalCost
      }
    }

    if (best) return best

    // Greedy set cover by coverage count, then refine cost
    const remaining = new Set(demands.map((d) => d.id))
    const chosen = new Set<string>()
    while (remaining.size > 0) {
      let bestSupplier = ''
      let bestCover = 0
      for (const supplier of suppliers) {
        if (chosen.has(supplier)) continue
        let cover = 0
        for (const demandId of remaining) {
          if ((byDemand.get(demandId) ?? []).some((o) => o.supplier === supplier)) cover += 1
        }
        if (cover > bestCover) {
          bestCover = cover
          bestSupplier = supplier
        }
      }
      if (!bestSupplier || bestCover === 0) break
      chosen.add(bestSupplier)
      for (const demandId of [...remaining]) {
        if ((byDemand.get(demandId) ?? []).some((o) => o.supplier === bestSupplier)) {
          remaining.delete(demandId)
        }
      }
    }

    const covered = this.assignIfCovered(demands, byDemand, chosen)
    if (covered) return covered

    return this.pickPerItem(demands, byDemand, (a, b) => a.unitPrice - b.unitPrice)
  }

  private bestScore(
    demands: OptimizerDemandItem[],
    byDemand: Map<string, SupplierOfferCandidate[]>,
  ): Assignment[] {
    const candidates: Assignment[][] = [
      this.pickPerItem(demands, byDemand, (a, b) => a.unitPrice - b.unitPrice),
      this.pickPerItem(
        demands,
        byDemand,
        (a, b) => a.deliveryDays - b.deliveryDays || a.unitPrice - b.unitPrice,
      ),
      this.minimizeFreight(demands, byDemand),
      this.minimizeSuppliers(demands, byDemand),
    ]

    const suppliers = this.allSuppliers(byDemand).slice(0, 8)
    for (const supplier of suppliers) {
      const one = this.assignIfCovered(demands, byDemand, new Set([supplier]))
      if (one) candidates.push(one)
    }

    let best = candidates[0] ?? []
    let bestScore = -1
    const scored = candidates.map((assignment) => {
      const cost = this.costOf(demands, assignment)
      return { assignment, cost }
    })

    const totals = scored.map((s) => s.cost.totalCost)
    const freights = scored.map((s) => s.cost.freightCost)
    const deliveries = scored.map((s) =>
      Math.max(0, ...s.assignment.map((a) => a.offer.deliveryDays)),
    )
    const counts = scored.map(
      (s) => new Set(s.assignment.map((a) => a.offer.supplier)).size,
    )

    const minTotal = Math.min(...totals)
    const maxTotal = Math.max(...totals)
    const minFreight = Math.min(...freights)
    const maxFreight = Math.max(...freights)
    const minDelivery = Math.min(...deliveries)
    const maxDelivery = Math.max(...deliveries)
    const minCount = Math.min(...counts)
    const maxCount = Math.max(...counts)

    for (let i = 0; i < scored.length; i++) {
      const priceScore = invertNorm(totals[i]!, minTotal, maxTotal)
      const freightScore = invertNorm(freights[i]!, minFreight, maxFreight)
      const deliveryScore = invertNorm(deliveries[i]!, minDelivery, maxDelivery)
      const countScore = invertNorm(counts[i]!, minCount, maxCount)
      const score =
        priceScore * this.weights.price +
        freightScore * this.weights.freight +
        deliveryScore * this.weights.delivery +
        countScore * this.weights.supplierCount
      if (score > bestScore) {
        bestScore = score
        best = scored[i]!.assignment
      }
    }

    return best
  }

  private assignIfCovered(
    demands: OptimizerDemandItem[],
    byDemand: Map<string, SupplierOfferCandidate[]>,
    suppliers: Set<string>,
  ): Assignment[] | null {
    const result: Assignment[] = []
    for (const demand of demands) {
      const offers = (byDemand.get(demand.id) ?? [])
        .filter((o) => suppliers.has(o.supplier))
        .sort((a, b) => a.unitPrice - b.unitPrice || a.deliveryDays - b.deliveryDays)
      const offer = offers[0]
      if (!offer) return null
      result.push({ demandId: demand.id, offer })
    }
    return result
  }

  private allSuppliers(byDemand: Map<string, SupplierOfferCandidate[]>): string[] {
    const set = new Set<string>()
    for (const offers of byDemand.values()) {
      for (const offer of offers) set.add(offer.supplier)
    }
    return [...set].sort()
  }

  private costOf(demands: OptimizerDemandItem[], assignment: Assignment[]) {
    return this.cost.calculate(this.toLines(demands, assignment))
  }

  private toLines(demands: OptimizerDemandItem[], assignment: Assignment[]): CostLineInput[] {
    const demandMap = new Map(demands.map((d) => [d.id, d]))
    return assignment.map(({ demandId, offer }) => {
      const demand = demandMap.get(demandId)!
      return {
        demandId,
        productId: demand.productId,
        product: demand.description,
        sku: demand.sku || offer.sku,
        supplier: offer.supplier,
        quantity: demand.quantity,
        unitPrice: offer.unitPrice,
        delivery: offer.deliveryDays,
      }
    })
  }

  private buildResult(
    demands: OptimizerDemandItem[],
    assignment: Assignment[],
    strategy: OptimizationStrategy,
    baselineTotal: number,
  ): OptimizationResult {
    const cost = this.costOf(demands, assignment)
    const supplierCount = new Set(assignment.map((a) => a.offer.supplier)).size
    const estimatedDelivery =
      assignment.length === 0 ? 0 : Math.max(...assignment.map((a) => a.offer.deliveryDays))
    const economy = round2(Math.max(0, baselineTotal - cost.totalCost))
    const economyPercent =
      baselineTotal > 0 ? round2((economy / baselineTotal) * 100) : 0

    const missing = demands.filter((d) => !assignment.some((a) => a.demandId === d.id))
    const warnings = [...cost.warnings]
    for (const demand of missing) {
      warnings.push(`Sem oferta com estoque para: ${demand.description}`)
    }

    return {
      strategy,
      totalCost: cost.totalCost,
      productCost: cost.productCost,
      freightCost: cost.freightCost,
      supplierCount,
      estimatedDelivery,
      economy,
      economyPercent,
      score: this.scoreResult(cost.totalCost, cost.freightCost, estimatedDelivery, supplierCount),
      items: cost.items,
      freightBySupplier: cost.freightBySupplier,
      warnings,
    }
  }

  private scoreResult(
    totalCost: number,
    freightCost: number,
    delivery: number,
    supplierCount: number,
  ): number {
    // Absolute heuristic score 0–100 for UI (weights applied relatively in MELHOR_SCORE)
    const pricePart = Math.max(0, 100 - totalCost / 200)
    const freightPart = Math.max(0, 100 - freightCost)
    const deliveryPart = Math.max(0, 100 - delivery * 4)
    const countPart = Math.max(0, 100 - supplierCount * 15)
    return round2(
      pricePart * this.weights.price +
        freightPart * this.weights.freight +
        deliveryPart * this.weights.delivery +
        countPart * this.weights.supplierCount,
    )
  }

  private normalize(weights: OptimizationWeights): OptimizationWeights {
    const sum = weights.price + weights.freight + weights.delivery + weights.supplierCount
    if (sum <= 0) return { ...DEFAULT_OPTIMIZATION_WEIGHTS }
    return {
      price: weights.price / sum,
      freight: weights.freight / sum,
      delivery: weights.delivery / sum,
      supplierCount: weights.supplierCount / sum,
    }
  }
}

function invertNorm(value: number, min: number, max: number): number {
  if (max === min) return 1
  return 1 - (value - min) / (max - min)
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const optimizationEngine = new OptimizationEngine()
